import { useEffect, type RefObject } from 'react'

interface ReturnSnapshot {
  scrollTop: number
  focusId: string
}

const snapshots = new Map<string, ReturnSnapshot>()

export function returnLocationKey(pathname: string, search: string) {
  return `${pathname}${search}`
}

export function captureDocumentReturn(
  locationKey: string,
  focusId: string,
  scrollContainer: HTMLElement | null,
) {
  snapshots.set(locationKey, {
    focusId,
    scrollTop: Math.max(0, scrollContainer?.scrollTop ?? 0),
  })
}

export function useRestoreDocumentReturn({
  locationKey,
  ready,
  scrollRef,
  fallbackRef,
}: {
  locationKey: string
  ready: boolean
  scrollRef: RefObject<HTMLElement | null>
  fallbackRef: RefObject<HTMLElement | null>
}) {
  useEffect(() => {
    if (!ready) return
    const snapshot = snapshots.get(locationKey)
    if (!snapshot) return
    snapshots.delete(locationKey)

    const frame = window.requestAnimationFrame(() => {
      const scrollContainer = scrollRef.current
      if (scrollContainer) scrollContainer.scrollTop = snapshot.scrollTop
      const candidates = scrollContainer?.querySelectorAll<HTMLElement>('[data-return-focus-id]')
      const target =
        Array.from(candidates ?? []).find(
          (candidate) => candidate.dataset.returnFocusId === snapshot.focusId,
        ) ?? fallbackRef.current
      target?.focus({ preventScroll: true })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [fallbackRef, locationKey, ready, scrollRef])
}
